import { useMemo, useState } from "react";
import { FileX2, Hourglass, TrendingDown, TrendingUp, UserMinus, UserPlus, Users } from "lucide-react";

import { usePageBack } from "../../context/PageBackContext.js";
import { cn } from "../../lib/utils.js";
import { reportAnchor, reportRange, shiftReportAnchor, REPORT_PERIOD_OPTIONS, type ReportPeriod } from "../../lib/reportRange.js";
import { DropdownMultiSelect, DropdownSelect } from "../../components/DropdownButton.js";
import { SpecularButton } from "../../components/ui/specular-button.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import { ChartPanel } from "../../components/ui/charts/ChartPanel.js";
import { TrendArea } from "../../components/ui/charts/TrendArea.js";
import {
  ReportPage, FilterBar, ErrorBanner, OrgFilters, ExportButton, CardGrid, ReportStatCard, ReportTableFrame, ReportFooter, PageSizeSelect, TableToolbar,
  dropdownProps, splitList, joinList, type OpenState,
} from "../../components/reports/ReportKit.js";
import { useUrlFilters, useReportData, useOrgMeta, toParams, downloadReport } from "../../components/reports/useReport.js";

interface HeadcountFigures {
  opening: number; joiners: number; exits: number; closing: number; net: number;
  active: number; onNotice: number; onboarding: number; onLeave: number; other: number;
}
interface HeadcountGroupRow extends HeadcountFigures { key: string; label: string }
interface HeadcountReport {
  from: string; to: string; groupBy: string; groupTitle: string;
  rows: HeadcountGroupRow[];
  totals: HeadcountFigures;
  cards: { closing: number; opening: number; joiners: number; exits: number; net: number; onProbation: number; withoutRecord: number };
  trend: { month: string; closing: number }[];
}

const DEFAULTS = {
  officeIds: "", departmentIds: "", employeeTypes: "", groupBy: "department", period: "month", anchor: "",
  page: "1", limit: "25",
};
const API = "/reports/hr/headcount";

const GROUP_OPTIONS = [
  { value: "department", label: "Group by Department" },
  { value: "office", label: "Group by Office" },
  { value: "employeeType", label: "Group by Employee type" },
  { value: "gender", label: "Group by Gender" },
  { value: "workLocation", label: "Group by Work location" },
];
const TYPE_OPTIONS = [
  { value: "FULL_TIME", label: "Full-time" },
  { value: "PART_TIME", label: "Part-time" },
  { value: "CONTRACTOR", label: "Contractor" },
  { value: "INTERN", label: "Intern" },
];

const TREND_MONTH = new Intl.DateTimeFormat("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });
const signed = (n: number) => (n > 0 ? `+${n}` : String(n));
const netClass = (n: number) => (n > 0 ? "text-[var(--color-success-fg)]" : n < 0 ? "text-[var(--color-error-fg)]" : "text-surface-500");

const NUM = "px-3 text-center tabular-nums";

/** A group's figures, in the column order of the table header. */
function FigureCells({ r }: { r: HeadcountFigures }) {
  return (
    <>
      <TableCell className={NUM}>{r.opening}</TableCell>
      <TableCell className={cn(NUM, r.joiners > 0 && "text-[var(--color-success-fg)]")}>{r.joiners}</TableCell>
      <TableCell className={cn(NUM, r.exits > 0 && "text-[var(--color-error-fg)]")}>{r.exits}</TableCell>
      <TableCell className={cn(NUM, "font-bold")}>{r.closing}</TableCell>
      <TableCell className={cn(NUM, "font-semibold", netClass(r.net))}>{signed(r.net)}</TableCell>
      <TableCell className={cn(NUM, "border-l border-surface-200")}>{r.active}</TableCell>
      <TableCell className={cn(NUM, r.onNotice > 0 && "text-[var(--color-warning-fg)]")}>{r.onNotice}</TableCell>
      <TableCell className={NUM}>{r.onboarding}</TableCell>
      <TableCell className={NUM}>{r.onLeave}</TableCell>
    </>
  );
}

export default function HeadcountReportPage() {
  usePageBack("/dashboard");
  const [f, setF] = useUrlFilters(DEFAULTS);
  const [open, setOpen] = useState<string | null>(null);
  const openState: OpenState = { open, setOpen };
  const org = useOrgMeta();

  const period = (REPORT_PERIOD_OPTIONS.some((o) => o.value === f.period) ? f.period : "month") as ReportPeriod;
  const anchor = f.anchor || reportAnchor(period);
  const range = reportRange(period, anchor);

  const params = useMemo(() => {
    // Paging is local (the report returns every group at once), so page / limit never refetch it.
    const { period: _p, anchor: _a, page: _pg, limit: _l, ...rest } = f;
    return toParams(rest, { from: range.from, to: range.to });
  }, [f, range.from, range.to]);
  const { data, loading, error } = useReportData<HeadcountReport>(API, params, "HeadcountReportPage");

  const rows = data?.rows ?? [];
  const limit = Math.max(1, Number(f.limit) || 25);
  const totalPages = Math.max(1, Math.ceil(rows.length / limit));
  const page = Math.min(Math.max(1, Number(f.page) || 1), totalPages);
  const pageRows = rows.slice((page - 1) * limit, page * limit);
  const meta = data ? { total: rows.length, page, limit, totalPages } : undefined;
  const c = data?.cards;
  const trend = useMemo(
    () => (data?.trend ?? []).map((t) => ({ label: TREND_MONTH.format(new Date(`${t.month}-01T00:00:00Z`)), value: t.closing })),
    [data?.trend],
  );

  return (
    <ReportPage
      title="Headcount Reports"
      description="Opening, joiners, exits and closing headcount for the period, by group, with the status mix at its end."
    >
      <div className="flex flex-col gap-3">
        <CardGrid>
          <ReportStatCard icon={Users} tone="brand" label="Closing Headcount" value={c?.closing ?? "—"} caption={c ? `${c.opening} at the start` : range.label} />
          <ReportStatCard icon={UserPlus} tone="success" label="Joiners" value={c?.joiners ?? "—"} caption={range.label} />
          <ReportStatCard icon={UserMinus} tone="danger" label="Exits" value={c?.exits ?? "—"} caption="By last working day" />
          <ReportStatCard
            icon={c && c.net < 0 ? TrendingDown : TrendingUp} tone={c && c.net < 0 ? "rose" : "info"}
            label="Net Change" value={c ? signed(c.net) : "—"} caption="Closing minus opening"
          />
          <ReportStatCard icon={Hourglass} tone="warning" label="On Probation" value={c?.onProbation ?? "—"} caption="At the end of the period" />
          <ReportStatCard
            icon={FileX2} tone="accent" label="No Offboarding Record" value={c?.withoutRecord ?? "—"}
            caption="Terminated without an exit date, so not counted"
          />
        </CardGrid>
        <ChartPanel title="Closing headcount" hint="End of each of the last 12 months">
          <TrendArea data={trend} formatValue={(n) => `${n} people`} formatTick={(n) => String(n)} height={200} />
        </ChartPanel>
      </div>

      <FilterBar>
        <OrgFilters meta={org} officeIds={f.officeIds} departmentIds={f.departmentIds} onChange={setF} openState={openState} />
        <DropdownMultiSelect
          values={splitList(f.employeeTypes)} onChange={(v) => setF({ employeeTypes: joinList(v) })} options={TYPE_OPTIONS}
          allLabel="All employee types" ariaLabel="Employee type" {...dropdownProps("type", openState)}
        />
        <DropdownSelect
          value={f.groupBy} onChange={(v) => setF({ groupBy: v })} options={GROUP_OPTIONS}
          placeholder="Group by" ariaLabel="Group by" clearable={false} {...dropdownProps("group", openState)}
        />
        <DropdownSelect
          value={period} onChange={(v) => setF({ period: v, anchor: "" })} options={REPORT_PERIOD_OPTIONS}
          placeholder="Period" ariaLabel="Period" clearable={false} {...dropdownProps("period", openState)}
        />
        <div className="flex items-center gap-3">
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor(period, anchor, -1) })} aria-label="Previous period" className="w-8 px-0">&larr;</SpecularButton>
          <div className="min-w-[150px] text-center text-sm font-semibold">{range.label}</div>
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor(period, anchor, 1) })} aria-label="Next period" className="w-8 px-0">&rarr;</SpecularButton>
        </div>
      </FilterBar>

      <TableToolbar
        left={<p className="text-[12px] text-surface-500">Groups use each employee&apos;s current department, office and details, not where they were on each date.</p>}
      >
        <PageSizeSelect limit={f.limit} onLimit={(v) => setF({ limit: v })} openState={openState} />
        <ExportButton onExport={() => downloadReport(API, params, `headcount-${range.from}-to-${range.to}.xlsx`)} />
      </TableToolbar>

      <ErrorBanner message={error} />

      <ReportTableFrame loading={loading} empty={rows.length === 0} emptyText={`No employees match these filters for ${range.label}.`} contentKey={data?.groupBy ?? ""}>
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow className="bg-surface-50">
              <TableHead rowSpan={2} className="px-3 align-bottom">{data?.groupTitle ?? "Group"}</TableHead>
              <TableHead colSpan={5} className="text-center">Movement</TableHead>
              <TableHead colSpan={4} className="border-l border-surface-200 text-center">Status at period end</TableHead>
            </TableRow>
            <TableRow className="bg-surface-50">
              <TableHead className="px-3 text-center">Opening</TableHead>
              <TableHead className="px-3 text-center">Joiners</TableHead>
              <TableHead className="px-3 text-center">Exits</TableHead>
              <TableHead className="px-3 text-center">Closing</TableHead>
              <TableHead className="px-3 text-center">Net</TableHead>
              <TableHead className="border-l border-surface-200 px-3 text-center">Active</TableHead>
              <TableHead className="px-3 text-center">On notice</TableHead>
              <TableHead className="px-3 text-center">Onboarding</TableHead>
              <TableHead className="px-3 text-center">On leave</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((r) => (
              <TableRow key={r.key || "none"} className="hover:bg-surface-50">
                <TableCell className="px-3 font-semibold text-surface-900">{r.label}</TableCell>
                <FigureCells r={r} />
              </TableRow>
            ))}
            {data && (
              <TableRow className="bg-surface-50 font-bold hover:bg-surface-50">
                <TableCell className="px-3 text-surface-900">Total</TableCell>
                <FigureCells r={data.totals} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ReportTableFrame>

      <ReportFooter meta={meta} onPage={(p) => setF({ page: String(p) })} />
    </ReportPage>
  );
}
